import React, { useState, useMemo } from 'react';
import { ChevronUp, ChevronDown, ChevronsUpDown } from 'lucide-react';

export default function DataTable({ data, loading, onAthleteClick }) {
  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 20;

  const sortedData = useMemo(() => {
    if (!sortConfig.key) return data;

    return [...data].sort((a, b) => {
      const aVal = a[sortConfig.key];
      const bVal = b[sortConfig.key];

      if (aVal === null || aVal === undefined) return 1;
      if (bVal === null || bVal === undefined) return -1;

      if (typeof aVal === 'number' && typeof bVal === 'number') {
        return sortConfig.direction === 'asc' ? aVal - bVal : bVal - aVal;
      }

      const aStr = String(aVal).toLowerCase();
      const bStr = String(bVal).toLowerCase();
      
      if (sortConfig.direction === 'asc') {
        return aStr.localeCompare(bStr);
      } else {
        return bStr.localeCompare(aStr);
      }
    });
  }, [data, sortConfig]);

  const paginatedData = useMemo(() => {
    const startIndex = (currentPage - 1) * itemsPerPage;
    return sortedData.slice(startIndex, startIndex + itemsPerPage);
  }, [sortedData, currentPage]);

  const totalPages = Math.ceil(sortedData.length / itemsPerPage);

  const handleSort = (key) => {
    setSortConfig(prev => ({
      key,
      direction: prev.key === key && prev.direction === 'asc' ? 'desc' : 'asc'
    }));
  };

  const SortIcon = ({ columnKey }) => {
    if (sortConfig.key !== columnKey) {
      return <ChevronsUpDown className="w-4 h-4 text-slate-500" />;
    }
    return sortConfig.direction === 'asc' 
      ? <ChevronUp className="w-4 h-4 text-blue-400" />
      : <ChevronDown className="w-4 h-4 text-blue-400" />;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-400 mx-auto mb-4"></div>
          <p className="text-slate-400">Loading data...</p>
        </div>
      </div>
    );
  }

  if (data.length === 0) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <p className="text-slate-400 text-lg mb-2">No data found</p>
          <p className="text-slate-500 text-sm">Try adjusting your filters</p>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full">
      {/* Table */}
      <div className="overflow-x-auto flex-1">
        <table className="w-full">
          <thead className="bg-slate-700/50 sticky top-0">
            <tr>
              <th 
                onClick={() => handleSort('student_id')}
                className="px-4 py-3 text-left text-xs font-semibold text-slate-300 uppercase tracking-wider cursor-pointer hover:bg-slate-600/50"
              >
                <div className="flex items-center space-x-1">
                  <span>ID</span>
                  <SortIcon columnKey="student_id" />
                </div>
              </th>
              <th 
                onClick={() => handleSort('first_name')}
                className="px-4 py-3 text-left text-xs font-semibold text-slate-300 uppercase tracking-wider cursor-pointer hover:bg-slate-600/50"
              >
                <div className="flex items-center space-x-1">
                  <span>Name</span>
                  <SortIcon columnKey="first_name" />
                </div>
              </th>
              <th 
                onClick={() => handleSort('gender')}
                className="px-4 py-3 text-left text-xs font-semibold text-slate-300 uppercase tracking-wider cursor-pointer hover:bg-slate-600/50"
              >
                <div className="flex items-center space-x-1">
                  <span>Gender</span>
                  <SortIcon columnKey="gender" />
                </div>
              </th>
              <th 
                onClick={() => handleSort('date_of_birth')}
                className="px-4 py-3 text-left text-xs font-semibold text-slate-300 uppercase tracking-wider cursor-pointer hover:bg-slate-600/50"
              >
                <div className="flex items-center space-x-1">
                  <span>Age</span>
                  <SortIcon columnKey="date_of_birth" />
                </div>
              </th>
              <th 
                onClick={() => handleSort('height_cm')}
                className="px-4 py-3 text-left text-xs font-semibold text-slate-300 uppercase tracking-wider cursor-pointer hover:bg-slate-600/50"
              >
                <div className="flex items-center space-x-1">
                  <span>Height</span>
                  <SortIcon columnKey="height_cm" />
                </div>
              </th>
              <th 
                onClick={() => handleSort('weight_kg')}
                className="px-4 py-3 text-left text-xs font-semibold text-slate-300 uppercase tracking-wider cursor-pointer hover:bg-slate-600/50"
              >
                <div className="flex items-center space-x-1">
                  <span>Weight</span>
                  <SortIcon columnKey="weight_kg" />
                </div>
              </th>
              <th 
                onClick={() => handleSort('vertical_jump_cm')}
                className="px-4 py-3 text-left text-xs font-semibold text-slate-300 uppercase tracking-wider cursor-pointer hover:bg-slate-600/50"
              >
                <div className="flex items-center space-x-1">
                  <span>V. Jump</span>
                  <SortIcon columnKey="vertical_jump_cm" />
                </div>
              </th>
              <th 
                onClick={() => handleSort('district_name')}
                className="px-4 py-3 text-left text-xs font-semibold text-slate-300 uppercase tracking-wider cursor-pointer hover:bg-slate-600/50"
              >
                <div className="flex items-center space-x-1">
                  <span>District</span>
                  <SortIcon columnKey="district_name" />
                </div>
              </th>
              <th className="px-4 py-3 text-left text-xs font-semibold text-slate-300 uppercase tracking-wider">
                School
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-700">
            {paginatedData.map((athlete, index) => {
              const age = athlete.date_of_birth ? new Date().getFullYear() - new Date(athlete.date_of_birth).getFullYear() : null;
              const formatNumber = (value, decimals = 1) => {
                const num = parseFloat(value);
                return !isNaN(num) && num !== null ? num.toFixed(decimals) : '-';
              };
              
              return (
                <tr 
                  key={athlete.id} 
                  onClick={() => onAthleteClick && onAthleteClick(athlete)}
                  className="hover:bg-slate-700/30 transition-colors cursor-pointer hover:bg-blue-900/20"
                  title="Click to view athlete profile"
                >
                  <td className="px-4 py-3 text-sm text-slate-400 font-mono">
                    {athlete.student_id || athlete.id || '-'}
                  </td>
                  <td className="px-4 py-3 text-sm text-white font-medium">
                    {athlete.first_name || '-'} {athlete.last_name || ''}
                  </td>
                  <td className="px-4 py-3 text-sm">
                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                      athlete.gender === 'Male' 
                        ? 'bg-blue-500/20 text-blue-300' 
                        : 'bg-pink-500/20 text-pink-300'
                    }`}>
                      {athlete.gender || '-'}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-sm text-slate-300">
                    {age ? age + ' yrs' : '-'}
                  </td>
                  <td className="px-4 py-3 text-sm text-slate-300">
                    {formatNumber(athlete.height_cm)} {athlete.height_cm ? 'cm' : ''}
                  </td>
                  <td className="px-4 py-3 text-sm text-slate-300">
                    {formatNumber(athlete.weight_kg)} {athlete.weight_kg ? 'kg' : ''}
                  </td>
                  <td className="px-4 py-3 text-sm text-slate-300">
                    {formatNumber(athlete.vertical_jump_cm)} {athlete.vertical_jump_cm ? 'cm' : ''}
                  </td>
                  <td className="px-4 py-3 text-sm text-slate-300">
                    {athlete.district_name || '-'}
                  </td>
                  <td className="px-4 py-3 text-sm text-slate-400 truncate max-w-xs">
                    {athlete.school_name || '-'}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="bg-slate-700/50 px-4 py-3 flex items-center justify-between border-t border-slate-700">
          <div className="text-sm text-slate-400">
            Showing {((currentPage - 1) * itemsPerPage) + 1} to {Math.min(currentPage * itemsPerPage, sortedData.length)} of {sortedData.length} results
          </div>
          <div className="flex items-center space-x-2">
            <button
              onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
              disabled={currentPage === 1}
              className="px-3 py-1 bg-slate-600 hover:bg-slate-500 text-white rounded disabled:opacity-50 disabled:cursor-not-allowed text-sm"
            >
              Previous
            </button>
            <span className="text-sm text-slate-300">
              Page {currentPage} of {totalPages}
            </span>
            <button
              onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
              disabled={currentPage === totalPages}
              className="px-3 py-1 bg-slate-600 hover:bg-slate-500 text-white rounded disabled:opacity-50 disabled:cursor-not-allowed text-sm"
            >
              Next
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
